import '../i18n';
import "@/styles/globals.css";
import type { AppProps } from "next/app";
import type { Session } from 'next-auth';
import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Nunito } from 'next/font/google';
import { getCookie } from 'cookies-next';
import i18next, { changeAppLanguage, rememberAppLanguage, resolveAppLanguage } from '../i18n';
import GoogleAnalytics from '@/components/GoogleAnalytics';
import PublicSupportBridge from '@/components/providers/PublicSupportBridge';

const nunito = Nunito({
  subsets: ['latin'],
  display: 'swap',
  weight: ['200', '300', '400', '500', '600', '700', '800', '900'],
  variable: '--font-nunito',
});

const PUBLIC_SEO_PATHS = new Set([
  '/',
  '/faq',
  '/contact',
  '/privacy-policy',
  '/terms-of-use',
  '/pricing',
  '/refund-policy',
  '/support',
  '/docs/oauth',
  '/mag',
  '/mag/[lang]/[slug]',
  '/help',
  '/help/[slug]',
  '/courses',
  '/courses/[slug]',
  '/language-learning-games',
  '/clubs',
  '/404',
]);

const PrivateRuntimeProviders = dynamic(
  () => Promise.all([
    import('@/components/providers/AppRuntimeProviders'),
    import('@/styles/editorjs.css'),
  ]).then(([module]) => module.default),
  { ssr: false },
);

const ClubRuntimeProviders = dynamic(
  () => import('@/components/providers/ClubRuntimeProviders'),
  { ssr: false },
);

function RuntimeGate({ children, session }: { children: React.ReactNode; session?: Session | null }) {
  const router = useRouter();
  const isPublicSeoPage = PUBLIC_SEO_PATHS.has(router.pathname);

  if (isPublicSeoPage) {
    return (
      <>
        <PublicSupportBridge />
        {children}
      </>
    );
  }

  if (router.pathname.startsWith('/clubs/')) {
    return <ClubRuntimeProviders session={session}>{children}</ClubRuntimeProviders>;
  }

  return <PrivateRuntimeProviders session={session}>{children}</PrivateRuntimeProviders>;
}

function MyApp({ Component, pageProps: { session, ...pageProps } }: AppProps) {
  const router = useRouter();

  // One source of truth: /tr, /de … set the language for the whole site and are remembered.
  useEffect(() => {
    const lang = resolveAppLanguage(router.locale, getCookie('language') as string | undefined);
    if (router.locale && router.locale !== 'en') rememberAppLanguage(router.locale, router.locales);
    void changeAppLanguage(lang);
  }, [router.locale, router.locales]);

  useEffect(() => {
    
    const updateDocument = () => {
      const currentLang = i18next.language || 'en';
      const direction = i18next.dir(currentLang);
      const isRtl = direction === 'rtl';
      document.documentElement.lang = currentLang;
      document.documentElement.dir = direction;
      document.body.classList.toggle('font-mikhak', isRtl);
      document.body.classList.toggle('font-nunito', !isRtl);
    };
    
    updateDocument();
    i18next.on('languageChanged', updateDocument);
    
    return () => i18next.off('languageChanged', updateDocument);
  }, []);

  return (
    <div className={`${nunito.variable} app-root`}>
      <GoogleAnalytics session={session} />
      <RuntimeGate session={session}>
        <Component {...pageProps} />
      </RuntimeGate>
    </div>
  )
    ;
}

export default MyApp;
